// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Uuid from "uuid";
import * as Curry from "@rescript/std/lib/es6/curry.js";
import * as React from "react";
import * as Segment from "../bindings/Segment.bs.js";
import * as Belt_Int from "@rescript/std/lib/es6/belt_Int.js";
import * as Caml_obj from "@rescript/std/lib/es6/caml_obj.js";
import * as Js_option from "@rescript/std/lib/es6/js_option.js";
import * as Belt_Array from "@rescript/std/lib/es6/belt_Array.js";
import * as Belt_Option from "@rescript/std/lib/es6/belt_Option.js";
import * as Caml_option from "@rescript/std/lib/es6/caml_option.js";
import * as FormHelpers from "./FormHelpers.bs.js";

function getGiftTheme(giftThemeData, giftThemeUid) {
  return Belt_Option.mapWithDefault(giftThemeData, "", (function (t) {
                var theme = Belt_Array.getBy(t.giftThemes, (function (s) {
                        return s.uid === FormHelpers.getValue(giftThemeUid);
                      }));
                return Belt_Option.mapWithDefault(theme, "", (function (theme) {
                              return theme.title;
                            }));
              }));
}

function isEmptyValue(value) {
  if (Caml_obj.equal(value, "")) {
    return true;
  } else {
    return value === null;
  }
}

function getSegmentCompletedOrderProduct(formValues, themes, card, href) {
  var giftTheme = getGiftTheme(themes, formValues.giftThemeUid);
  var doesThemeExistInTemplates = Belt_Array.some(card.themes, (function (x) {
          return x.uid === formValues.themeUid;
        }));
  var isCustomSkuThemeDesign = doesThemeExistInTemplates ? "false" : "true";
  return Object.assign(Segment.cardToProduct(card), {
              url: href,
              gift_theme: giftTheme,
              price: Belt_Int.fromString(formValues.value),
              sku_theme_design_uid: formValues.themeUid,
              is_custom_sku_theme_design: isCustomSkuThemeDesign
            });
}

function getSegmentBuyForKey(state) {
  var match = state.senderAlias;
  if (typeof match !== "number") {
    return "anonymousgift";
  }
  switch (match) {
    case /* Myself */0 :
        return "myself";
    case /* Gift */1 :
        return "gift";
    case /* SenderAliasNotSelected */2 :
        return "";
    
  }
}

function makeSegmentData(state, purchaseInstanceId) {
  return {
          step_name: "PCbuyingFlowSelectSelfPurchaseOrGift",
          buy_for: getSegmentBuyForKey(state),
          purchase_instance_id: purchaseInstanceId,
          step: 1
        };
}

function callbackIfTypeSelected(state, segmentCallback, purchaseInstanceId) {
  var match = state.senderAlias;
  if (typeof match === "number" && match >= 2) {
    return false;
  }
  var trackingObj = makeSegmentData(state, purchaseInstanceId);
  Curry._1(segmentCallback, trackingObj);
  return true;
}

var BuyFor = {
  getSegmentBuyForKey: getSegmentBuyForKey,
  makeSegmentData: makeSegmentData,
  callbackIfTypeSelected: callbackIfTypeSelected
};

function getCheckoutStartedProduct(card, href, state) {
  return Object.assign(Segment.cardToProduct(card), {
              url: href,
              price: Belt_Int.fromString(Belt_Option.getWithDefault(state.purchasePrice, "0"))
            });
}

function sendSegmentCheckoutStarted(product, value, callback, purchaseInstanceId) {
  Curry._1(callback, {
        affiliation: "consumer",
        total: Belt_Int.fromString(Belt_Option.getWithDefault((value == null) ? undefined : Caml_option.some(value), "0")),
        currency: process.env.NEXT_PUBLIC_APP_CURRENCY_CODE,
        purchase_instance_id: purchaseInstanceId,
        products: [product]
      });
}

function customThemeImageUrlExists(imageUrl) {
  return Belt_Option.mapWithDefault((imageUrl == null) ? undefined : Caml_option.some(imageUrl), "false", (function (image) {
                if (image === "") {
                  return "false";
                } else {
                  return "true";
                }
              }));
}

function setSelectedThemeData(product, card, themeUid, customThemeUid, customThemeImageUrl) {
  var defaultTheme = Belt_Option.mapWithDefault((themeUid == null) ? undefined : Caml_option.some(themeUid), undefined, (function (themeUid) {
          return Belt_Array.getBy(card.themes, (function (theme) {
                        return theme.uid === themeUid;
                      }));
        }));
  if (defaultTheme !== undefined) {
    return Object.assign(product, {
                image_uid: defaultTheme.uid,
                image_url: defaultTheme.image,
                image_card_upload: "false"
              });
  }
  var imageUid = Belt_Option.mapWithDefault((customThemeUid == null) ? undefined : Caml_option.some(customThemeUid), "", (function (themeUid) {
          return themeUid;
        }));
  var imageUrl = Belt_Option.mapWithDefault((customThemeImageUrl == null) ? undefined : Caml_option.some(customThemeImageUrl), "", (function (themeImageUrl) {
          return themeImageUrl;
        }));
  return Object.assign(product, {
              image_uid: imageUid,
              image_url: imageUrl,
              image_card_upload: customThemeImageUrlExists(customThemeImageUrl)
            });
}

function getProductFromCard(formValues, themes, card, href) {
  var giftTheme = getGiftTheme(themes, formValues.giftThemeUid);
  var doesThemeExistInTemplates = Belt_Array.some(card.themes, (function (x) {
          return x.uid === formValues.themeUid;
        }));
  var isCustomSkuThemeDesign = doesThemeExistInTemplates ? "false" : "true";
  return Object.assign(Segment.cardToProduct(card), {
              url: href,
              gift_theme: giftTheme,
              price: Belt_Int.fromString(formValues.value),
              sku_theme_design_uid: formValues.themeUid,
              is_custom_sku_theme_design: isCustomSkuThemeDesign
            });
}

function sendSegmentCompletedOrder(state, formValues, orderResultUid, orderResultTotal, product, callback, purchaseInstanceId) {
  var coupon = Belt_Option.mapWithDefault(Caml_option.nullable_to_opt(formValues.promo), "", (function (p) {
          return p.promoCode;
        }));
  var buyFor = getSegmentBuyForKey(state);
  var giftMediaUid = formValues.giftMediaUid;
  var videoMessage = (giftMediaUid == null) ? "false" : "true";
  var scheduledDeliveryAt = formValues.scheduledDeliveryAt;
  var deliveryMethod = (scheduledDeliveryAt == null) ? "now" : "schedule";
  var reciContactShared = Belt_Option.mapWithDefault(Caml_option.nullable_to_opt(formValues.phone), "false", (function (phone) {
          if (phone.number !== "") {
            return "true";
          } else {
            return "false";
          }
        }));
  var guestEmailCtx = Belt_Option.mapWithDefault(Caml_option.nullable_to_opt(formValues.guestEmail), {}, (function (p) {
          return {
                  traits: {
                    email: p
                  }
                };
        }));
  var ttclid = localStorage.getItem("ttclid");
  var ttclid$1 = (ttclid == null) ? undefined : Caml_option.some(ttclid);
  localStorage.removeItem("ttclid");
  var defaultProps = {
    coupon: coupon,
    buy_for: buyFor,
    video_message: videoMessage,
    gift_theme_uid: formValues.giftThemeUid,
    delivery_method: deliveryMethod,
    recipient_phonedetail_fields: reciContactShared,
    purchase_instance_id: purchaseInstanceId,
    order_number: orderResultUid,
    affiliation: "consumer",
    total: Belt_Int.fromString(orderResultTotal),
    currency: process.env.NEXT_PUBLIC_APP_CURRENCY_CODE,
    products: [product],
    server_region: process.env.NEXT_PUBLIC_APP_COUNTRY_CODE,
    store: "consumer"
  };
  var props = Js_option.isNone(ttclid$1) || ttclid$1 === "" ? defaultProps : Object.assign(defaultProps, {
          ttclid: ttclid$1
        });
  return Curry._2(callback, props, guestEmailCtx);
}

function generateUniqueUserId(param) {
  var timeStamp = Date.now().toString();
  var uuid = Uuid.v4();
  return timeStamp.concat(uuid);
}

function useProductPageSegment(state, card, form, step1Callback, purchaseInstanceId, preselectedValue) {
  var value = Curry._2(form.watch, "value", preselectedValue);
  var prevValue = React.useRef(value);
  var themeUid = Curry._2(form.watch, "themeUid", "");
  var customThemeUid = Curry._2(form.watch, "customThemeUid", "");
  var customThemeImageUrl = Curry._2(form.watch, "customThemeImageUrl", "");
  var sendSegmentProductAdded = function (card) {
    Segment.productAdded(setSelectedThemeData(Object.assign(Segment.cardToProductNoImageUrl(card), {
                  url: document.location.href,
                  purchase_instance_id: purchaseInstanceId,
                  price: Belt_Int.fromString(Belt_Option.getWithDefault((value == null) ? undefined : Caml_option.some(value), "0")),
                  store: "consumer"
                }), card, themeUid, customThemeUid, customThemeImageUrl));
  };
  React.useEffect((function () {
          setTimeout((function (param) {
                  if (preselectedValue !== "") {
                    return sendSegmentProductAdded(card);
                  }
                  
                }), 700);
        }), []);
  React.useEffect((function () {
          if (state.senderAlias !== /* SenderAliasNotSelected */2) {
            var product = getCheckoutStartedProduct(card, document.location.href, state);
            sendSegmentCheckoutStarted(product, value, Segment.checkoutStarted, purchaseInstanceId);
            Curry._3(step1Callback, state, Segment.checkoutStepCompleted, purchaseInstanceId);
          }
          
        }), [state.senderAlias]);
  React.useEffect((function () {
          var match = state.orderResult;
          var orderSent = match !== undefined;
          if (!isEmptyValue(prevValue.current) && Caml_obj.notequal(value, prevValue.current) && !orderSent) {
            Segment.productRemoved(Object.assign(Segment.cardToProduct(card), {
                      url: document.location.href,
                      purchase_instance_id: purchaseInstanceId,
                      price: Belt_Int.fromString(Belt_Option.getWithDefault(Caml_option.nullable_to_opt(prevValue.current), "0"))
                    }));
          }
          if (!isEmptyValue(value) && Caml_obj.notequal(prevValue.current, value)) {
            sendSegmentProductAdded(card);
          }
          prevValue.current = value;
        }), [value]);
}

export {
  getGiftTheme ,
  isEmptyValue ,
  getSegmentCompletedOrderProduct ,
  BuyFor ,
  getCheckoutStartedProduct ,
  sendSegmentCheckoutStarted ,
  customThemeImageUrlExists ,
  setSelectedThemeData ,
  getProductFromCard ,
  sendSegmentCompletedOrder ,
  generateUniqueUserId ,
  useProductPageSegment ,
}
/* uuid Not a pure module */
